// The fake api's sales pipeline (F-HQ-39, spec/14 §16), past the HQ guard in `fake-server.ts` (`hq:leads`): leads, their
// timeline, the message templates, Today's queue, the funnel and the CSV import — all in memory. Bodies and queries are
// checked with the contract's own schemas, and it keeps the real service's rules where a screen can meet them: a phone is
// read as an Indian number and stored as E.164 (`400` on `phone` when it is not one), one another lead has is
// `409 lead_phone_taken`, `lost` needs a reason, a first message counts against a daily cap, and a lead marked do-not-contact
// is never queued or messaged. Responses are the contract's own shapes.

import type { Lead, LeadActivity, LeadTemplate } from '@clavian/api-client';
import {
  ChangeLeadStageSchema,
  ConvertLeadSchema,
  CreateLeadSchema,
  CreateLeadTemplateSchema,
  ImportLeadsSchema,
  LeadActivitySchema,
  LeadFunnelSchema,
  LeadImportResultSchema,
  LeadListQuerySchema,
  LeadOutreachSchema,
  LeadQueueSchema,
  LeadSchema,
  LeadTemplateListQuerySchema,
  LeadTemplateSchema,
  LogLeadActivitySchema,
  OpenLeadOutreachSchema,
  UpdateLeadSchema,
  UpdateLeadTemplateSchema,
  type LeadStage,
  type z,
} from '@clavian/contracts';
import { canMoveLeadStage, isOpenLeadStage, normalizeIndianPhone, whatsAppUrl } from '@clavian/domain/leads';

import type { CatalogueHelpers } from './fake-hq-catalogue';
import type { FakeResponse } from './fake-server';

/** HQ's one member who can own a lead, as the fake's HQ member is called. */
export const LEAD_ASSIGNEE = { id: '0199a000-0000-7000-8000-000000000001', name: 'Asha Rao', role: 'hq_admin' } as const;

const ID_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
const DAILY_CAP = 20;

let counter = 0;
function typeId(prefix: string): string {
  counter += 1;
  let n = counter;
  let tail = '';
  for (let index = 0; index < 4; index += 1) {
    tail = (ID_ALPHABET[n % 32] ?? '0') + tail;
    n = Math.floor(n / 32);
  }
  return `${prefix}_01k5znw4hmfay8vvr14d2p${tail}`;
}

const DAY = 24 * 60 * 60 * 1000;
const at = (offsetMs: number): string => new Date(Date.now() + offsetMs).toISOString();

type LeadSeed = Partial<Lead> & Pick<Lead, 'businessName'>;

/** A lead as the api returns it, with what the seed leaves out filled in. */
export function leadFixture(seed: LeadSeed): Lead {
  const phone = seed.phone ?? null;
  const mobile = phone === null ? false : /^\+91[6-9]/.test(phone);
  return LeadSchema.parse({
    id: typeId('lead'),
    contactName: null,
    phone,
    phoneMobile: mobile,
    email: null,
    instagram: null,
    city: null,
    area: null,
    addressHint: null,
    stationCount: null,
    platforms: [],
    currentSoftware: null,
    googleRatingTenths: null,
    googleReviewCount: null,
    mapUrl: null,
    hook: null,
    priority: 'c',
    stage: 'new',
    lostReason: null,
    ownerUserId: null,
    ownerName: null,
    nextFollowUpAt: null,
    lastContactedAt: null,
    doNotContact: false,
    doNotContactAt: null,
    doNotContactReason: null,
    source: 'manual',
    organizationId: null,
    organizationName: null,
    createdByUserId: LEAD_ASSIGNEE.id,
    createdAt: at(-10 * DAY),
    updatedAt: at(-1 * DAY),
    ...seed,
  });
}

/** A small, varied pipeline: every open stage, a won and a lost lead, two cities, a landline and a do-not-contact. */
export function seedLeads(): Lead[] {
  return [
    leadFixture({ businessName: 'Level Up Lounge', instagram: 'levelup.lounge', contactName: 'Anika Rao', stationCount: 14, platforms: ['PC', 'PS5'], currentSoftware: 'Pen and paper', mapUrl: 'https://maps.example/level-up', city: 'Pune', area: 'Koregaon Park', phone: '+919876543210', priority: 'a', stage: 'new', googleRatingTenths: 46, googleReviewCount: 312, hook: 'Your PS5 corner looks great.' }),
    leadFixture({ businessName: 'Respawn Cafe', city: 'Pune', area: 'Viman Nagar', phone: '+919811122233', priority: 'b', stage: 'contacted', googleRatingTenths: 44, googleReviewCount: 120, lastContactedAt: at(-3 * DAY), nextFollowUpAt: at(-1 * DAY), ownerUserId: LEAD_ASSIGNEE.id, ownerName: LEAD_ASSIGNEE.name }),
    leadFixture({ businessName: 'Pixel Palace', email: 'hello@pixelpalace.example', city: 'Mumbai', area: 'Andheri West', phone: '+919922200011', priority: 'a', stage: 'replied', googleRatingTenths: 47, googleReviewCount: 540, lastContactedAt: at(-2 * DAY), nextFollowUpAt: at(2 * DAY) }),
    leadFixture({ businessName: 'Arcade Alley', city: 'Mumbai', area: 'Bandra', phone: '+912245678901', priority: 'c', stage: 'demo', googleRatingTenths: 41, googleReviewCount: 58, nextFollowUpAt: at(5 * DAY) }),
    leadFixture({ businessName: 'Controller Cove', city: 'Bengaluru', area: 'Indiranagar', phone: '+919700011122', priority: 'b', stage: 'proposal', googleRatingTenths: 45, googleReviewCount: 210 }),
    leadFixture({ businessName: 'Nitro Nights', city: 'Bengaluru', area: 'Koramangala', phone: '+919633300044', priority: 'a', stage: 'won', googleRatingTenths: 48, googleReviewCount: 890 }),
    leadFixture({ businessName: 'Old School Games', city: 'Pune', area: 'Kothrud', phone: '+919544411100', priority: 'c', stage: 'lost', lostReason: 'price', googleRatingTenths: 39, googleReviewCount: 22 }),
    leadFixture({ businessName: 'Quiet Quest', city: 'Delhi', area: 'Hauz Khas', phone: '+919455566677', priority: 'c', stage: 'new', doNotContact: true, doNotContactAt: at(-4 * DAY), doNotContactReason: 'Asked us not to message.' }),
  ];
}

export interface FakeLeadsOptions {
  /** No lead, no template: the feature's first day. */
  readonly empty?: boolean;
  /** Leads to start with, in place of the seed. */
  readonly leads?: readonly Lead[];
  /** Start with this template list (otherwise none). */
  readonly templates?: readonly LeadTemplate[];
  /** Makes the organisation a conversion creates (a `POST /v1/hq/organizations` through the directory). */
  readonly createOrganization?: (body: unknown) => FakeResponse;
}

const invalid = (helpers: CatalogueHelpers, error: z.ZodError) =>
  helpers.problem(400, 'validation_failed', 'Validation failed', {
    detail: 'The request could not be understood as written.',
    errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
  });

/** A comma-aware CSV line split (quotes, doubled quotes). */
function csvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index] ?? '';
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"') quoted = true;
    else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field);
      field = '';
      if (row.some((cell) => cell.trim() !== '')) rows.push(row);
      row = [];
    } else field += char;
  }
  row.push(field);
  if (row.some((cell) => cell.trim() !== '')) rows.push(row);
  return rows;
}

/** The headings the import recognises, by their plain form (lower case, letters only), and the lead field each fills. */
const IMPORT_COLUMNS: Readonly<Record<string, string>> = {
  businessname: 'businessName',
  name: 'businessName',
  business: 'businessName',
  phone: 'phone',
  mobile: 'phone',
  city: 'city',
  area: 'area',
  contact: 'contactName',
  contactname: 'contactName',
  email: 'email',
  instagram: 'instagram',
  address: 'addressHint',
  stations: 'stationCount',
  platforms: 'platforms',
  currentsoftware: 'currentSoftware',
  googlerating: 'googleRating',
  googlereviews: 'googleReviewCount',
  maplink: 'mapUrl',
  hook: 'hook',
};

export function createFakeHqLeads(options: FakeLeadsOptions = {}) {
  const leads: Lead[] = options.empty === true ? [] : [...(options.leads ?? seedLeads())];
  const state = {
    leads,
    activities: new Map<string, LeadActivity[]>(),
    templates: [...(options.templates ?? [])] as LeadTemplate[],
    /** First messages opened today, against the daily cap. */
    firstMessagesToday: 0,
    dailyCap: DAILY_CAP,
    /** Every request body the fake accepted, by operation, so a test can assert what the screen sent. */
    sent: [] as { readonly operation: string; readonly body: unknown }[],
  };

  const activitiesOf = (leadId: string): LeadActivity[] => {
    let list = state.activities.get(leadId);
    if (list === undefined) {
      list = [];
      state.activities.set(leadId, list);
    }
    return list;
  };

  function record(lead: Lead, activity: Partial<LeadActivity> & Pick<LeadActivity, 'kind'>): LeadActivity {
    const now = new Date().toISOString();
    const made = LeadActivitySchema.parse({
      id: typeId('lact'),
      channel: null,
      templateId: null,
      templateName: null,
      firstContact: false,
      body: null,
      fromStage: null,
      toStage: null,
      actorUserId: LEAD_ASSIGNEE.id,
      actorName: LEAD_ASSIGNEE.name,
      occurredAt: now,
      createdAt: now,
      ...activity,
    });
    activitiesOf(lead.id).unshift(made);
    return made;
  }

  if (options.empty !== true && options.leads === undefined) {
    // A lived-in timeline for the café that was messaged: the message, the move it caused, and a note.
    const pixel = state.leads.find((lead) => lead.businessName === 'Pixel Palace');
    if (pixel !== undefined) {
      activitiesOf(pixel.id).push(
        LeadActivitySchema.parse({ id: typeId('lact'), kind: 'message', channel: 'whatsapp', templateId: null, templateName: 'First message', firstContact: true, body: 'Hi Pixel Palace, this is Asha from Clavian.', fromStage: null, toStage: null, actorUserId: LEAD_ASSIGNEE.id, actorName: LEAD_ASSIGNEE.name, occurredAt: at(-2 * DAY), createdAt: at(-2 * DAY) }),
      );
    }
    const respawn = state.leads.find((lead) => lead.businessName === 'Respawn Cafe');
    if (respawn !== undefined) {
      const list = activitiesOf(respawn.id);
      const stamp = (offset: number) => at(offset);
      const make = (activity: Partial<LeadActivity> & Pick<LeadActivity, 'kind'>, offset: number) =>
        LeadActivitySchema.parse({
          id: typeId('lact'),
          channel: null,
          templateId: null,
          templateName: null,
          firstContact: false,
          body: null,
          fromStage: null,
          toStage: null,
          actorUserId: LEAD_ASSIGNEE.id,
          actorName: LEAD_ASSIGNEE.name,
          occurredAt: stamp(offset),
          createdAt: stamp(offset),
          ...activity,
        });
      list.push(
        make({ kind: 'note', body: 'Owner is travelling until Friday. Try again then.' }, -1 * DAY),
        make({ kind: 'stage_change', fromStage: 'new', toStage: 'contacted' }, -3 * DAY),
        make({ kind: 'message', channel: 'whatsapp', templateName: 'First message', firstContact: true, body: 'Hi Respawn, this is Asha from Clavian. Would you be open to a quick look at how it works?' }, -3 * DAY),
      );
    }
  }

  const summaryOf = (lead: Lead) => ({
    id: lead.id,
    businessName: lead.businessName,
    city: lead.city,
    area: lead.area,
    phone: lead.phone,
    phoneMobile: lead.phoneMobile,
    instagram: lead.instagram,
    priority: lead.priority,
    stage: lead.stage,
    ownerUserId: lead.ownerUserId,
    ownerName: lead.ownerName,
    nextFollowUpAt: lead.nextFollowUpAt,
    lastContactedAt: lead.lastContactedAt,
    googleRatingTenths: lead.googleRatingTenths,
    googleReviewCount: lead.googleReviewCount,
    doNotContact: lead.doNotContact,
    organizationId: lead.organizationId,
  });

  /** Reads the phone as written. `undefined`: not given; `null`: cleared; a string: the number; `false`: not a number. */
  function readPhone(value: string | null | undefined): { e164: string | null; mobile: boolean } | undefined | false {
    if (value === undefined) return undefined;
    if (value === null || value.trim() === '') return { e164: null, mobile: false };
    const phone = normalizeIndianPhone(value);
    return phone === null ? false : { e164: phone.e164, mobile: phone.mobile };
  }

  const handleOf = (value: string | null | undefined): string | null | undefined => {
    if (value === undefined || value === null) return value;
    const match = /(?:instagram\.com\/)?@?([A-Za-z0-9._]+)\/?$/.exec(value.trim());
    return match?.[1] ?? value.trim();
  };

  const fieldsOf = (body: Record<string, unknown>): Partial<Lead> => {
    const out: Record<string, unknown> = {};
    for (const key of [
      'contactName', 'email', 'city', 'area', 'addressHint', 'stationCount', 'platforms', 'currentSoftware', 'googleRatingTenths',
      'googleReviewCount', 'mapUrl', 'hook', 'priority', 'ownerUserId', 'nextFollowUpAt',
    ]) {
      if (body[key] !== undefined) out[key] = body[key];
    }
    if (typeof out['contactName'] === 'string' && out['contactName'] === '') out['contactName'] = null;
    if ('ownerUserId' in out) {
      out['ownerName'] = out['ownerUserId'] === LEAD_ASSIGNEE.id ? LEAD_ASSIGNEE.name : null;
      if (out['ownerUserId'] !== null && out['ownerUserId'] !== LEAD_ASSIGNEE.id) out['ownerUserId'] = null;
    }
    return out as Partial<Lead>;
  };

  function handle(helpers: CatalogueHelpers, method: string, pathname: string, searchParams: URLSearchParams, input: unknown): FakeResponse | undefined {
    const { json, problem } = helpers;
    const sent = (operation: string) => state.sent.push({ operation, body: input });
    const noLead = () => problem(404, 'not_found', 'No such lead.');

    // -- templates ---------------------------------------------------------------------------
    if (pathname === '/v1/hq/lead-templates') {
      if (method === 'GET') {
        const query = LeadTemplateListQuerySchema.safeParse(Object.fromEntries(searchParams));
        if (!query.success) return invalid(helpers, query.error);
        const all = query.data.includeArchived === 'true';
        return json(200, { data: state.templates.filter((one) => all || one.archivedAt === null) });
      }
      if (method === 'POST') {
        const body = CreateLeadTemplateSchema.safeParse(input);
        if (!body.success) return invalid(helpers, body.error);
        sent('createHqLeadTemplate');
        const now = new Date().toISOString();
        const made = LeadTemplateSchema.parse({ id: typeId('ltpl'), ...body.data, archivedAt: null, createdAt: now, updatedAt: now });
        state.templates.push(made);
        return json(201, made);
      }
    }
    const template = /^\/v1\/hq\/lead-templates\/([^/]+)(\/archive)?$/.exec(pathname);
    if (template !== null) {
      const found = state.templates.find((one) => one.id === template[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such template.');
      if (template[2] === '/archive' && method === 'POST') {
        sent('archiveHqLeadTemplate');
        found.archivedAt = new Date().toISOString();
        return json(200, found);
      }
      if (template[2] === undefined && method === 'PATCH') {
        const body = UpdateLeadTemplateSchema.safeParse(input);
        if (!body.success) return invalid(helpers, body.error);
        sent('updateHqLeadTemplate');
        Object.assign(found, body.data, { updatedAt: new Date().toISOString() });
        return json(200, found);
      }
    }

    // -- collection routes --------------------------------------------------------------------
    if (pathname === '/v1/hq/leads' && method === 'GET') {
      const query = LeadListQuerySchema.safeParse(Object.fromEntries(searchParams));
      if (!query.success) return invalid(helpers, query.error);
      const { stage, city, priority, ownerId, doNotContact, q, sort, cursor, limit } = query.data;
      const needle = q?.toLowerCase();
      const digitsOnly = (q?.replace(/\D/g, '') ?? '').replace(/^0+/, '');
      let rows = state.leads.filter(
        (lead) =>
          (stage === undefined || (stage === 'open' ? isOpenLeadStage(lead.stage) : lead.stage === stage)) &&
          (city === undefined || lead.city === city) &&
          (priority === undefined || lead.priority === priority) &&
          (ownerId === undefined || lead.ownerUserId === ownerId) &&
          (doNotContact === undefined || lead.doNotContact === (doNotContact === 'true')) &&
          (needle === undefined ||
            `${lead.businessName} ${lead.area ?? ''} ${lead.instagram ?? ''}`.toLowerCase().includes(needle) ||
            (digitsOnly.length >= 3 && (lead.phone ?? '').replace(/\D/g, '').includes(digitsOnly))),
      );
      const reviews = (lead: Lead) => lead.googleReviewCount ?? 0;
      rows = [...rows].sort((a, b) => {
        if (sort === 'updated') return b.updatedAt.localeCompare(a.updatedAt);
        if (sort === 'reviews') return reviews(b) - reviews(a);
        const x = a.nextFollowUpAt ?? '9999';
        const y = b.nextFollowUpAt ?? '9999';
        return x === y ? reviews(b) - reviews(a) : x.localeCompare(y);
      });
      const offset = cursor === undefined ? 0 : Number(cursor);
      const page = rows.slice(offset, offset + limit);
      const more = offset + limit < rows.length;
      return json(200, { data: page.map(summaryOf), total: rows.length, page: { cursor: more ? String(offset + limit) : null, hasMore: more, count: page.length } });
    }

    if (pathname === '/v1/hq/leads' && method === 'POST') {
      const body = CreateLeadSchema.safeParse(input);
      if (!body.success) return invalid(helpers, body.error);
      const phone = readPhone(body.data.phone);
      if (phone === false) return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'phone', code: 'custom', message: 'That is not an Indian phone number.' }] });
      if (phone?.e164 != null) {
        const taken = state.leads.find((lead) => lead.phone === phone.e164);
        if (taken !== undefined) return problem(409, 'lead_phone_taken', 'Another lead has that phone number.', { detail: `${taken.businessName} already has this phone number.` });
      }
      sent('createHqLead');
      const { phone: _phone, instagram, ...rest } = body.data;
      const lead = leadFixture({
        ...fieldsOf(rest as Record<string, unknown>),
        businessName: body.data.businessName,
        phone: phone?.e164 ?? null,
        phoneMobile: phone?.mobile ?? false,
        instagram: handleOf(instagram) ?? null,
        platforms: body.data.platforms ?? [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      state.leads.unshift(lead);
      return json(201, lead);
    }

    if (pathname === '/v1/hq/leads/import' && method === 'POST') {
      const body = ImportLeadsSchema.safeParse(input);
      if (!body.success) return invalid(helpers, body.error);
      if (!/\.csv$/i.test(body.data.fileName)) return problem(422, 'migration_file_unreadable', 'The file cannot be read.');
      let text: string;
      try {
        text = new TextDecoder().decode(Uint8Array.from(atob(body.data.contentBase64), (char) => char.charCodeAt(0)));
      } catch {
        return problem(422, 'migration_file_unreadable', 'The file cannot be read.');
      }
      const rows = csvRows(text);
      const header = rows[0];
      if (header === undefined) return problem(422, 'migration_file_unreadable', 'The file is empty.');
      const fieldAt = header.map((cell) => IMPORT_COLUMNS[cell.toLowerCase().replace(/[^a-z]/g, '')]);
      const columns = { matched: header.filter((_cell, index) => fieldAt[index] !== undefined), ignored: header.filter((_cell, index) => fieldAt[index] === undefined) };
      const data = rows.slice(1);
      if (data.length > 2000) return problem(422, 'migration_file_unreadable', 'Too many rows.');
      const duplicates: { row: number; businessName: string; reason: string }[] = [];
      const refused: { row: number; reason: string }[] = [];
      const fresh: Lead[] = [];
      const phonesSeen = new Set(state.leads.map((lead) => lead.phone).filter((phone): phone is string => phone !== null));
      data.forEach((cells, index) => {
        const row = index + 2;
        const value = (field: string): string => cells[fieldAt.indexOf(field)]?.trim() ?? '';
        const businessName = value('businessName');
        if (businessName === '') {
          refused.push({ row, reason: 'No business name.' });
          return;
        }
        const phone = readPhone(value('phone'));
        if (phone === false) {
          refused.push({ row, reason: 'The phone number is not an Indian number.' });
          return;
        }
        if (phone?.e164 != null) {
          if (phonesSeen.has(phone.e164)) {
            duplicates.push({ row, businessName, reason: 'This phone number is already a lead.' });
            return;
          }
          phonesSeen.add(phone.e164);
        }
        const rating = value('googleRating');
        const reviewsCount = value('googleReviewCount');
        fresh.push(
          leadFixture({
            businessName,
            city: value('city') || null,
            area: value('area') || null,
            contactName: value('contactName') || null,
            email: value('email') || null,
            instagram: handleOf(value('instagram')) ?? null,
            phone: phone?.e164 ?? null,
            phoneMobile: phone?.mobile ?? false,
            hook: value('hook') || null,
            googleRatingTenths: rating === '' ? null : Math.round(Number(rating) * 10),
            googleReviewCount: reviewsCount === '' ? null : Number(reviewsCount),
            source: 'import',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
        );
      });
      if (!body.data.dryRun) {
        sent('importHqLeads');
        state.leads.unshift(...fresh);
      }
      return json(200, LeadImportResultSchema.parse({ total: data.length, added: fresh.length, duplicates, refused, columns }));
    }

    if (pathname === '/v1/hq/leads/cities' && method === 'GET') {
      const counts = new Map<string, number>();
      for (const lead of state.leads) if (lead.city !== null) counts.set(lead.city, (counts.get(lead.city) ?? 0) + 1);
      const data = [...counts].map(([city, count]) => ({ city, count })).sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
      return json(200, { data });
    }

    if (pathname === '/v1/hq/leads/assignees' && method === 'GET') return json(200, { data: [LEAD_ASSIGNEE] });

    if (pathname === '/v1/hq/leads/queue' && method === 'GET') {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const due = state.leads
        .filter((lead) => isOpenLeadStage(lead.stage) && !lead.doNotContact && lead.nextFollowUpAt !== null && new Date(lead.nextFollowUpAt) <= endOfToday)
        .sort((a, b) => (a.nextFollowUpAt ?? '').localeCompare(b.nextFollowUpAt ?? ''));
      const rank = { a: 0, b: 1, c: 2 } as const;
      const fresh = state.leads
        .filter((lead) => lead.stage === 'new' && !lead.doNotContact && (lead.phone !== null || lead.instagram !== null))
        .sort((a, b) => rank[a.priority] - rank[b.priority] || (b.googleReviewCount ?? 0) - (a.googleReviewCount ?? 0));
      return json(
        200,
        LeadQueueSchema.parse({
          due: due.slice(0, 50).map(summaryOf),
          fresh: fresh.slice(0, 50).map(summaryOf),
          firstMessagesLeftToday: Math.max(0, state.dailyCap - state.firstMessagesToday),
          dailyCap: state.dailyCap,
        }),
      );
    }

    if (pathname === '/v1/hq/leads/funnel' && method === 'GET') {
      const stages: LeadStage[] = ['new', 'contacted', 'replied', 'demo', 'proposal', 'won', 'lost', 'parked'];
      const cities = new Map<string, { total: number; open: number; won: number }>();
      for (const lead of state.leads) {
        const row = cities.get(lead.city ?? '') ?? { total: 0, open: 0, won: 0 };
        row.total += 1;
        if (isOpenLeadStage(lead.stage)) row.open += 1;
        if (lead.stage === 'won') row.won += 1;
        cities.set(lead.city ?? '', row);
      }
      const everContacted = state.leads.filter((lead) => lead.stage !== 'new').length;
      const everReplied = state.leads.filter((lead) => ['replied', 'demo', 'proposal', 'won'].includes(lead.stage)).length;
      return json(
        200,
        LeadFunnelSchema.parse({
          byStage: stages.map((one) => ({ stage: one, count: state.leads.filter((lead) => lead.stage === one).length })),
          byCity: [...cities].filter(([city]) => city !== '').map(([city, row]) => ({ city, ...row })),
          byTemplate: (() => {
            const groups = new Map<string, { templateId: string | null; templateName: string | null; channel: 'whatsapp' | 'instagram' | 'email'; sent: number; replied: number }>();
            for (const lead of state.leads) {
              const replied = ['replied', 'demo', 'proposal', 'won'].includes(lead.stage);
              const last = (state.activities.get(lead.id) ?? []).filter((one) => one.kind === 'message').at(0);
              for (const one of state.activities.get(lead.id) ?? []) {
                if (one.kind !== 'message' || one.channel === null || one.channel === 'call' || one.channel === 'visit') continue;
                const key = `${one.templateId ?? one.templateName ?? 'none'}|${one.channel}`;
                const group = groups.get(key) ?? { templateId: one.templateId, templateName: one.templateName, channel: one.channel, sent: 0, replied: 0 };
                group.sent += 1;
                if (replied && last === one) group.replied += 1;
                groups.set(key, group);
              }
            }
            return [...groups.values()];
          })(),
          totals: { leads: state.leads.length, contacted: everContacted, replied: everReplied, won: state.leads.filter((lead) => lead.stage === 'won').length },
        }),
      );
    }

    // -- one lead -----------------------------------------------------------------------------
    const one = /^\/v1\/hq\/leads\/([^/]+)(?:\/(stage|activities|outreach|convert))?$/.exec(pathname);
    if (one === null) return undefined;
    const lead = state.leads.find((row) => row.id === one[1]);
    const part = one[2];

    if (part === undefined) {
      if (lead === undefined) return noLead();
      if (method === 'GET') return json(200, lead);
      if (method === 'DELETE') {
        state.leads.splice(state.leads.indexOf(lead), 1);
        state.activities.delete(lead.id);
        sent('deleteHqLead');
        return json(204, null);
      }
      if (method === 'PATCH') {
        const body = UpdateLeadSchema.safeParse(input);
        if (!body.success) return invalid(helpers, body.error);
        const phone = readPhone(body.data.phone);
        if (phone === false) return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'phone', code: 'custom', message: 'That is not an Indian phone number.' }] });
        if (phone?.e164 != null) {
          const taken = state.leads.find((row) => row.phone === phone.e164 && row.id !== lead.id);
          if (taken !== undefined) return problem(409, 'lead_phone_taken', 'Another lead has that phone number.', { detail: `${taken.businessName} already has this phone number.` });
        }
        if (body.data.doNotContact === true && body.data.doNotContactReason === undefined) {
          return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'doNotContactReason', code: 'custom', message: 'Say why.' }] });
        }
        sent('updateHqLead');
        const patch: Partial<Lead> = { ...fieldsOf(body.data as Record<string, unknown>) };
        if (body.data.businessName !== undefined) patch.businessName = body.data.businessName;
        if (phone !== undefined) Object.assign(patch, { phone: phone.e164, phoneMobile: phone.mobile });
        if (body.data.instagram !== undefined) patch.instagram = handleOf(body.data.instagram) ?? null;
        if (body.data.doNotContact === true) {
          Object.assign(patch, { doNotContact: true, doNotContactAt: new Date().toISOString(), doNotContactReason: body.data.doNotContactReason ?? null });
          record(lead, { kind: 'do_not_contact', body: body.data.doNotContactReason ?? null });
        } else if (body.data.doNotContact === false) {
          Object.assign(patch, { doNotContact: false, doNotContactAt: null, doNotContactReason: null });
          record(lead, { kind: 'do_not_contact', body: null });
        }
        // As the server writes it: the instant itself, in the text of the entry.
        if (body.data.nextFollowUpAt !== undefined && body.data.nextFollowUpAt !== lead.nextFollowUpAt) {
          const when = body.data.nextFollowUpAt === null ? null : new Date(body.data.nextFollowUpAt).toISOString();
          record(lead, { kind: 'follow_up', body: when === null ? 'Follow-up cleared' : `Follow-up set for ${when}` });
        }
        Object.assign(lead, patch, { updatedAt: new Date().toISOString() });
        return json(200, lead);
      }
    }

    if (lead === undefined) return noLead();

    if (part === 'stage' && method === 'POST') {
      const body = ChangeLeadStageSchema.safeParse(input);
      if (!body.success) return invalid(helpers, body.error);
      if (body.data.stage === 'lost' && body.data.lostReason === undefined) {
        return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'lostReason', code: 'custom', message: 'Say why it was lost.' }] });
      }
      if (!canMoveLeadStage(lead.stage, body.data.stage)) return problem(409, 'conflict', 'The lead cannot move there.', { detail: `A ${lead.stage} lead cannot move to ${body.data.stage}.` });
      sent('changeHqLeadStage');
      const from = lead.stage;
      Object.assign(lead, { stage: body.data.stage, lostReason: body.data.stage === 'lost' ? (body.data.lostReason ?? null) : null, updatedAt: new Date().toISOString() });
      // As the server does: moving to contacted by hand means it was contacted.
      if (body.data.stage === 'contacted') {
        lead.lastContactedAt = new Date().toISOString();
        if (lead.nextFollowUpAt === null) lead.nextFollowUpAt = at(3 * DAY);
      }
      record(lead, { kind: 'stage_change', fromStage: from, toStage: body.data.stage, body: body.data.note ?? null });
      return json(200, lead);
    }

    if (part === 'activities') {
      if (method === 'GET') {
        const list = activitiesOf(lead.id);
        const cursor = searchParams.get('cursor');
        const limit = Number(searchParams.get('limit') ?? 25);
        const offset = cursor === null ? 0 : Number(cursor);
        const slice = list.slice(offset, offset + limit);
        const more = offset + limit < list.length;
        return json(200, { data: slice, page: { cursor: more ? String(offset + limit) : null, hasMore: more, count: slice.length } });
      }
      if (method === 'POST') {
        const body = LogLeadActivitySchema.safeParse(input);
        if (!body.success) return invalid(helpers, body.error);
        sent('logHqLeadActivity');
        const occurredAt = body.data.occurredAt ?? new Date().toISOString();
        const made = record(lead, { kind: body.data.kind, channel: body.data.channel ?? null, body: body.data.body ?? null, occurredAt });
        if (body.data.kind === 'reply' && lead.stage === 'contacted') {
          const from = lead.stage;
          lead.stage = 'replied';
          record(lead, { kind: 'stage_change', fromStage: from, toStage: 'replied' });
        }
        if (body.data.kind === 'call' || body.data.kind === 'visit') lead.lastContactedAt = occurredAt;
        lead.updatedAt = new Date().toISOString();
        return json(201, made);
      }
    }

    if (part === 'outreach' && method === 'POST') {
      const body = OpenLeadOutreachSchema.safeParse(input);
      if (!body.success) return invalid(helpers, body.error);
      if (lead.doNotContact) return problem(409, 'do_not_contact', 'This lead asked not to be contacted.');
      const firstContact = lead.stage === 'new';
      if (firstContact && state.firstMessagesToday >= state.dailyCap) return problem(409, 'daily_outreach_cap', 'You have used today’s first messages.');
      if (body.data.channel === 'whatsapp' && (lead.phone === null || !lead.phoneMobile)) return problem(409, 'lead_no_phone', 'This lead has no mobile number.');
      if (body.data.channel === 'instagram' && lead.instagram === null) return problem(409, 'lead_no_instagram', 'This lead has no Instagram handle.');
      sent('openHqLeadOutreach');
      const used = state.templates.find((one) => one.id === body.data.templateId);
      const activity = record(lead, { kind: 'message', channel: body.data.channel, body: body.data.text, templateId: used?.id ?? null, templateName: used?.name ?? null, firstContact });
      if (firstContact) {
        state.firstMessagesToday += 1;
        lead.stage = 'contacted';
      }
      lead.lastContactedAt = new Date().toISOString();
      lead.updatedAt = lead.lastContactedAt;
      if (firstContact && lead.nextFollowUpAt === null) lead.nextFollowUpAt = at(3 * DAY);
      return json(
        200,
        LeadOutreachSchema.parse({
          lead,
          activity,
          whatsAppUrl: body.data.channel === 'whatsapp' && lead.phone !== null ? whatsAppUrl(lead.phone, body.data.text) : null,
          instagramUrl: body.data.channel === 'instagram' && lead.instagram !== null ? `https://instagram.com/${lead.instagram}` : null,
          firstMessagesLeftToday: Math.max(0, state.dailyCap - state.firstMessagesToday),
        }),
      );
    }

    if (part === 'convert' && method === 'POST') {
      if (lead.organizationId !== null) return problem(409, 'lead_converted', 'This lead already became an organisation.');
      const convert = ConvertLeadSchema.safeParse(input);
      if (!convert.success) return invalid(helpers, convert.error);
      const made = options.createOrganization?.(convert.data);
      if (made === undefined) return problem(501, 'not_implemented', 'Not available in this fake.');
      if (made.status !== 201) return made;
      sent('convertHqLead');
      const created = JSON.parse(made.body) as { organization: { id: string; name: string }; ownerInvitation: unknown };
      const from = lead.stage;
      Object.assign(lead, { stage: 'won', organizationId: created.organization.id, organizationName: created.organization.name, updatedAt: new Date().toISOString() });
      record(lead, { kind: 'conversion' });
      if (from !== 'won') record(lead, { kind: 'stage_change', fromStage: from, toStage: 'won' });
      return json(201, { lead, organization: created.organization, ownerInvitation: created.ownerInvitation });
    }

    return undefined;
  }

  return { state, handle };
}
